// The layout of the missions rings: pure, synchronous and deterministic.
// The response comes in; out come the half-axes of the five rings, the
// position of every capability and category, and the lines between them.
// The same response always gives the same positions.
//
// The capability picked stands at the centre. The missions its hosts serve
// stand on the inner ring; platform and infrastructure capabilities on the
// second; module, component and unknown-level ones on the third; the
// categories that provide them on the two outer rings, by the nature of
// their class: platforms, infrastructure and equipment, the whole end items,
// on the fourth ring, and what is fitted into them (modules, parts,
// materials, software, and a category of no nature) on the outer ring. An
// empty ring is not drawn and takes no room: the next ring stands where it
// would have. A ring is an ellipse, wider than tall, as the ecosystem's are:
// a name is written level beneath its mark, so neighbours side by side need
// more room than neighbours above one another.
//
// The turn is shared among the effect areas, each a sector by the number of
// its capabilities (missions among them), in the order of their names.
// Within its sector an area's capabilities of each ring are spread evenly,
// by function area and then by lift, so the three inner rings line up: a
// mission and a platform behaviour stand in the middle of the module and
// component capabilities of their area. A category wants the mean angle of
// the capabilities it provides. Then each ring is spread to a least angular
// gap, keeping its order and moving each node as little as the gap allows,
// so that no two names collide.

import {
  MissionCapability,
  MissionCategory,
  MissionEdge,
  MissionLevel,
  Missions,
} from "../../../types/analytics";
import { RING_ASPECT } from "../Ecosystem/ecosystemLayout";

export const TURN = 2 * Math.PI;
// The top of a ring: angles are measured from it, clockwise
const TOP = -Math.PI / 2;

// The rings: 1 inner (missions), 2 (platform behaviours), 3 (module and
// component capabilities), 4 (platform, infrastructure and equipment
// categories), 5 outer (the categories of what is fitted into them)
export type MissionRing = 1 | 2 | 3 | 4 | 5;
export type CapabilityRing = 1 | 2 | 3;
export type CategoryRing = 4 | 5;

export const MISSION_RINGS: readonly MissionRing[] = [1, 2, 3, 4, 5];
const CAPABILITY_RINGS: readonly CapabilityRing[] = [1, 2, 3];
const CATEGORY_RINGS: readonly CategoryRing[] = [4, 5];

export const ringOfLevel = (level: MissionLevel): CapabilityRing =>
  level === "operator" ? 1 : level === "platform" || level === "infrastructure" ? 2 : 3;

// The natures of the category backbone's level 1 that are whole end items,
// operated on their own: a category of one stands on the fourth ring
export const END_ITEM_NATURES: ReadonlySet<string> = new Set([
  "platform",
  "infrastructure",
  "equipment",
  "consumer_good",
  "service",
]);

export const ringOfCategory = (category: Pick<MissionCategory, "nature">): CategoryRing =>
  category.nature !== null && END_ITEM_NATURES.has(category.nature) ? 4 : 5;

// The width a name wraps within, beneath its mark
export const LABEL_WIDTH = 130;
// The least distance between two neighbours of a ring: a name's width and
// a little air. Two marks this far apart never overlap their names, the
// rings being wider than tall.
const CHORD = 150;
// The least height of the innermost ring drawn above the centre: clear of
// the centre's circle and the lines in it
const MIN_INNER = 130;
// The least a ring stands above the ring drawn inside it: the capability
// rings hold small marks, the category rings a box and two lines of name
const MIN_STEP_MIDDLE = 90;
const MIN_STEP_OUTER = 120;
const STEP_TO: Record<MissionRing, number> = {
  1: MIN_STEP_MIDDLE,
  2: MIN_STEP_MIDDLE,
  3: MIN_STEP_MIDDLE,
  4: MIN_STEP_OUTER,
  5: MIN_STEP_OUTER,
};
// The share of the turn the capabilities of a ring may take when it is
// sized: the rest is slack, so that they can stay near the angles their
// areas give them
const CAPABILITY_FILL = 0.6;

// The widths of a line, from its least count to its most
export const MIN_WIDTH = 1.2;
export const MAX_WIDTH = 3;

export interface PlacedMissionRing {
  ring: MissionRing;
  // Half its width and half its height
  rx: number;
  ry: number;
  // Nodes on it
  held: number;
}

export interface PlacedCapability {
  capability: MissionCapability;
  ring: CapabilityRing;
  // From the top, clockwise, within one turn
  angle: number;
  x: number;
  y: number;
}

export interface PlacedCategory {
  category: MissionCategory;
  ring: CategoryRing;
  angle: number;
  // The mean angle of the capabilities it provides; null with none drawn
  wanted: number | null;
  x: number;
  y: number;
}

// A line as drawn: a spoke from the centre to a capability, or a provider
// edge from a capability to a category
export interface PlacedMissionEdge {
  id: string;
  kind: "spoke" | "provider";
  capability: string;
  // The category of a provider edge
  category: string | null;
  // The provider edge itself; null for a spoke
  edge: MissionEdge | null;
  // The lift of a spoke's capability, or the count of a provider edge
  // (hosts inside, products that could)
  width: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface MissionsLayout {
  rings: PlacedMissionRing[];
  capabilities: PlacedCapability[];
  categories: PlacedCategory[];
  edges: PlacedMissionEdge[];
  // The least angle between two categories of each category ring
  categoryGaps: Record<CategoryRing, number>;
}

// An angle within one turn from the top
const withinTurn = (angle: number): number => ((angle % TURN) + TURN) % TURN;

// How far apart two angles are, the short way round
export const angularDistance = (a: number, b: number): number => {
  const turn = Math.abs(a - b) % TURN;
  return Math.min(turn, TURN - turn);
};

// The mean of angles on the circle; null when they cancel out
export const circularMean = (angles: number[]): number | null => {
  const x = angles.reduce((sum, angle) => sum + Math.cos(angle), 0);
  const y = angles.reduce((sum, angle) => sum + Math.sin(angle), 0);
  if (angles.length === 0 || Math.hypot(x, y) < 1e-9 * angles.length) {
    return null;
  }
  return withinTurn(Math.atan2(y, x));
};

// Plain string order, the same under every locale
const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

// A named reference as a key that sorts by name, the unnamed last
const sortName = (ref: { id: string; name: string | null } | null): string =>
  ref ? `0${ref.name ?? ""}\u0000${ref.id}` : "1";

// The half-height of a ring that holds `count` nodes using `fill` of the
// turn, no lower than `floor`
const heightFor = (floor: number, count: number, fill: number): number =>
  count < 2 ? floor : Math.max(floor, CHORD / (2 * Math.sin((TURN / (2 * count)) * fill)));

// The least angle two neighbours of a ring of half-height `ry` stand apart
const gapAt = (ry: number): number => 2 * Math.asin(Math.min(1, CHORD / (2 * ry)));

// Isotonic regression: the non-decreasing sequence nearest the values, by
// pooling adjacent values that run backwards
const nonDecreasing = (values: number[]): number[] => {
  const blocks: { sum: number; size: number }[] = [];
  for (const value of values) {
    blocks.push({ sum: value, size: 1 });
    while (
      blocks.length > 1 &&
      blocks[blocks.length - 2].sum / blocks[blocks.length - 2].size >
        blocks[blocks.length - 1].sum / blocks[blocks.length - 1].size
    ) {
      const last = blocks.pop();
      if (last) {
        blocks[blocks.length - 1].sum += last.sum;
        blocks[blocks.length - 1].size += last.size;
      }
    }
  }
  return blocks.flatMap((block) => Array(block.size).fill(block.sum / block.size));
};

// The angles of nodes spread round a ring to a least gap: each in the order
// of the angles wanted, and as near its wanted angle as the gap allows (the
// least squares). The ring is cut at the widest gap between the angles
// wanted, which no node needs to cross. Where the gap leaves no room to
// move, the nodes stand evenly round the turn in the same order.
export const spreadRound = (
  wanted: { key: string; angle: number }[],
  gap: number,
): Map<string, number> => {
  const count = wanted.length;
  if (count === 0) {
    return new Map();
  }
  const least = Math.min(gap, TURN / count);
  const sorted = wanted
    .map((entry) => ({ key: entry.key, angle: withinTurn(entry.angle) }))
    .sort((a, b) => a.angle - b.angle || compareText(a.key, b.key));
  let start = 0;
  let widest = -1;
  sorted.forEach((entry, index) => {
    const next = sorted[(index + 1) % count];
    const room = index + 1 < count ? next.angle - entry.angle : next.angle + TURN - entry.angle;
    if (room > widest) {
      widest = room;
      start = (index + 1) % count;
    }
  });
  // Unwrapped from the cut, so that the angles rise
  const rising = sorted.map((_entry, step) => {
    const index = (start + step) % count;
    return { key: sorted[index].key, angle: sorted[index].angle + (start + step >= count ? TURN : 0) };
  });
  const fitted = nonDecreasing(rising.map((entry, step) => entry.angle - step * least));
  let angles = fitted.map((value, step) => value + step * least);
  if (angles[count - 1] - angles[0] > TURN - least + 1e-9) {
    const step = TURN / count;
    const offset =
      rising.reduce((sum, entry, index) => sum + entry.angle - index * step, 0) / count;
    angles = rising.map((_entry, index) => offset + index * step);
  }
  return new Map(rising.map((entry, index) => [entry.key, withinTurn(angles[index])]));
};

// The point of a ring at an angle from its top, clockwise
const pointAt = (rx: number, ry: number, angle: number): { x: number; y: number } => ({
  x: rx * Math.cos(TOP + angle),
  y: ry * Math.sin(TOP + angle),
});

// Rounded to a tenth of a pixel, so that positions compare stably
const round = (value: number): number => Math.round(value * 10) / 10;

// A width between the least and the most, by where the value stands between
// the least and the most of its kind; the middle when they are one
const widthOf = (value: number, least: number, most: number): number =>
  most <= least
    ? (MIN_WIDTH + MAX_WIDTH) / 2
    : MIN_WIDTH + ((value - least) / (most - least)) * (MAX_WIDTH - MIN_WIDTH);

// What a provider edge counts: the hosts with a part inside, or the products
// that could provide
export const edgeCount = (edge: MissionEdge): number =>
  edge.provider === "inside" ? edge.hosts : edge.products;

// The angles the capabilities want: the turn shared among the effect areas
// by their capabilities, each ring's capabilities of an area spread evenly
// over its sector, by function area, then lift, name and id
const capabilityAims = (capabilities: MissionCapability[]): Map<string, number> => {
  const aims = new Map<string, number>();
  const total = capabilities.length;
  const areas = Array.from(new Set(capabilities.map((entry) => sortName(entry.effect_area)))).sort(
    compareText,
  );
  let from = 0;
  for (const area of areas) {
    const held = capabilities
      .filter((entry) => sortName(entry.effect_area) === area)
      .sort(
        (a, b) =>
          compareText(sortName(a.function_area), sortName(b.function_area)) ||
          b.lift - a.lift ||
          compareText(a.capability.name ?? "", b.capability.name ?? "") ||
          compareText(a.id, b.id),
      );
    const room = (TURN * held.length) / total;
    for (const ring of CAPABILITY_RINGS) {
      const onRing = held.filter((entry) => ringOfLevel(entry.level) === ring);
      onRing.forEach((entry, index) => {
        aims.set(entry.id, from + (room * (index + 0.5)) / onRing.length);
      });
    }
    from += room;
  }
  return aims;
};

export const layoutMissions = (missions: Missions): MissionsLayout => {
  const onCapabilityRing = (ring: CapabilityRing) =>
    missions.capabilities.filter((entry) => ringOfLevel(entry.level) === ring);
  const onCategoryRing = (ring: CategoryRing) =>
    missions.categories.filter((entry) => ringOfCategory(entry) === ring);
  const capabilityRings: Record<CapabilityRing, MissionCapability[]> = {
    1: onCapabilityRing(1),
    2: onCapabilityRing(2),
    3: onCapabilityRing(3),
  };
  const categoryRings: Record<CategoryRing, MissionCategory[]> = {
    4: onCategoryRing(4),
    5: onCategoryRing(5),
  };
  const held: Record<MissionRing, number> = {
    1: capabilityRings[1].length,
    2: capabilityRings[2].length,
    3: capabilityRings[3].length,
    4: categoryRings[4].length,
    5: categoryRings[5].length,
  };
  // Each ring stands a step outside the last ring drawn, the innermost at
  // the least height. An empty ring is not drawn and takes no room: it is
  // given the height it would have had, and the next ring stands there too.
  const heights = {} as Record<MissionRing, number>;
  let inside: number | null = null;
  for (const ring of MISSION_RINGS) {
    const floor = inside === null ? MIN_INNER : inside + STEP_TO[ring];
    heights[ring] = heightFor(floor, held[ring], ring <= 3 ? CAPABILITY_FILL : 1);
    if (held[ring] > 0) {
      inside = heights[ring];
    }
  }

  const aims = capabilityAims(missions.capabilities);
  const capabilityAngles = new Map<string, number>();
  for (const ring of CAPABILITY_RINGS) {
    const onRing = capabilityRings[ring];
    const spread = spreadRound(
      onRing.map((entry) => ({ key: entry.id, angle: aims.get(entry.id) ?? 0 })),
      gapAt(heights[ring]),
    );
    spread.forEach((angle, id) => capabilityAngles.set(id, angle));
  }
  const capabilities = missions.capabilities.map((capability): PlacedCapability => {
    const ring = ringOfLevel(capability.level);
    const angle = capabilityAngles.get(capability.id) ?? 0;
    const point = pointAt(heights[ring] * RING_ASPECT, heights[ring], angle);
    return { capability, ring, angle, x: round(point.x), y: round(point.y) };
  });

  // A category wants the mean angle of the capabilities it provides; one
  // that provides none drawn keeps its place in the list round the turn
  const provided = new Map<string, Set<string>>();
  for (const edge of missions.edges) {
    if (capabilityAngles.has(edge.capability)) {
      provided.set(edge.category, (provided.get(edge.category) ?? new Set()).add(edge.capability));
    }
  }
  const wanted = new Map<string, number | null>(
    missions.categories.map((category) => [
      category.id,
      circularMean(
        Array.from(provided.get(category.id) ?? []).map((id) => capabilityAngles.get(id) ?? 0),
      ),
    ]),
  );
  // Each category ring is spread on its own
  const categoryGaps: Record<CategoryRing, number> = { 4: 0, 5: 0 };
  const categoryAngles = new Map<string, number>();
  for (const ring of CATEGORY_RINGS) {
    const onRing = categoryRings[ring];
    categoryGaps[ring] = Math.min(gapAt(heights[ring]), TURN / Math.max(1, onRing.length));
    const spread = spreadRound(
      onRing.map((category, index) => ({
        key: category.id,
        angle: wanted.get(category.id) ?? (TURN * index) / onRing.length,
      })),
      categoryGaps[ring],
    );
    spread.forEach((angle, id) => categoryAngles.set(id, angle));
  }
  const categories = missions.categories.map((category): PlacedCategory => {
    const ring = ringOfCategory(category);
    const angle = categoryAngles.get(category.id) ?? 0;
    const point = pointAt(heights[ring] * RING_ASPECT, heights[ring], angle);
    return {
      category,
      ring,
      angle,
      wanted: wanted.get(category.id) ?? null,
      x: round(point.x),
      y: round(point.y),
    };
  });

  const capabilityAt = new Map(capabilities.map((placed) => [placed.capability.id, placed]));
  const categoryAt = new Map(categories.map((placed) => [placed.category.id, placed]));
  const lifts = missions.capabilities.map((entry) => entry.lift);
  const spokes = capabilities.map(
    (placed): PlacedMissionEdge => ({
      id: `spoke:${placed.capability.id}`,
      kind: "spoke",
      capability: placed.capability.id,
      category: null,
      edge: null,
      width: round(widthOf(placed.capability.lift, Math.min(...lifts), Math.max(...lifts))),
      x1: 0,
      y1: 0,
      x2: placed.x,
      y2: placed.y,
    }),
  );
  const mostCount = Math.max(1, ...missions.edges.map(edgeCount));
  const providers = missions.edges.flatMap((edge): PlacedMissionEdge[] => {
    const from = capabilityAt.get(edge.capability);
    const to = categoryAt.get(edge.category);
    if (!from || !to) {
      return [];
    }
    return [
      {
        id: `${edge.provider}:${edge.capability}>${edge.category}`,
        kind: "provider",
        capability: edge.capability,
        category: edge.category,
        edge,
        width: round(widthOf(edgeCount(edge), 0, mostCount)),
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
      },
    ];
  });

  const rings: PlacedMissionRing[] = MISSION_RINGS.map((ring) => ({
    ring,
    rx: round(heights[ring] * RING_ASPECT),
    ry: round(heights[ring]),
    held: held[ring],
  }));

  return { rings, capabilities, categories, edges: [...spokes, ...providers], categoryGaps };
};
